import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { dataApi } from "@/lib/agenda";

const AppStateContext = createContext(null);

export const AppStateProvider = ({ children }) => {
  const [appointments, setAppointments] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [appts, ters, cls, evs] = await Promise.all([
        dataApi.listAppointments(),
        dataApi.listTherapists(),
        dataApi.listClients(),
        dataApi.listEvents(),
      ]);
      setAppointments(appts);
      setTherapists(ters);
      setClients(cls);
      setEvents(evs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addAppointment = useCallback(async (data) => {
    const created = await dataApi.createAppointment(data);
    setAppointments((list) => [...list, created]);
    return created;
  }, []);

  const updateAppointment = useCallback(async (id, data) => {
    const updated = await dataApi.updateAppointment(id, data);
    setAppointments((list) => list.map((a) => (a.id === id ? { ...a, ...updated } : a)));
    return updated;
  }, []);

  const deleteAppointment = useCallback(async (id) => {
    await dataApi.deleteAppointment(id);
    setAppointments((list) => list.filter((a) => a.id !== id));
  }, []);

  const addTherapist = useCallback(async (data) => {
    const created = await dataApi.createTherapist(data);
    setTherapists((list) => [...list, created]);
    return created;
  }, []);

  const deleteTherapist = useCallback(async (id) => {
    await dataApi.deleteTherapist(id);
    setTherapists((list) => list.filter((t) => t.id !== id));
  }, []);

  const updateTherapist = useCallback(async (id, data) => {
    const updated = await dataApi.updateTherapist(id, data);
    setTherapists((list) => list.map((t) => (t.id === id ? { ...t, ...updated } : t)));
    return updated;
  }, []);

  const addClient = useCallback(async (data) => {
    const created = await dataApi.createClient(data);
    setClients((list) => [...list, created]);
    return created;
  }, []);

  const updateClient = useCallback(async (id, data) => {
    const updated = await dataApi.updateClient(id, data);
    setClients((list) => list.map((c) => (c.id === id ? { ...c, ...updated } : c)));
    return updated;
  }, []);

  const deleteClient = useCallback(async (id) => {
    await dataApi.deleteClient(id);
    setClients((list) => list.filter((c) => c.id !== id));
  }, []);

  const addEvent = useCallback(async (data) => {
    const created = await dataApi.createEvent(data);
    setEvents((list) => [...list, created]);
    return created;
  }, []);

  const deleteEvent = useCallback(async (id) => {
    await dataApi.deleteEvent(id);
    setEvents((list) => list.filter((e) => e.id !== id));
  }, []);

  return (
    <AppStateContext.Provider
      value={{
        appointments,
        therapists,
        clients,
        events,
        loading,
        addAppointment,
        updateAppointment,
        deleteAppointment,
        addTherapist,
        updateTherapist,
        deleteTherapist,
        addClient,
        updateClient,
        deleteClient,
        addEvent,
        deleteEvent,
      }}
    >
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => useContext(AppStateContext);
